import { useHistory } from 'react-router-dom';
import { getDatasetControlDatasetActionTemplateData } from '../../../../api/utils/requestData/getDatasetControlDatasetActionTemplateData';
import {
  DatasetDataApiRequestData,
  SubsetDataApiRequestData,
} from '../../../../api/types/types';
import {
  EXTERNAL_APP_CONTROL_PARAM,
  EXTERNAL_APP_SCOPE_PARAM,
  EXTERNAL_APP_SUBSET_PARAM,
  EXTERNAL_APP_URL_PARAM,
} from '../../../../constants';
import { debugLogger } from '../../../../debug/DebugLogger';
import {
  ApplicationControlAction,
  Dataset,
  DatasetControl,
  JSONObject,
} from '../../../../types/types';
import { resolveAppActionUrl } from '../../../../utils/datasetControl/resolveAppActionUrl';
import useRoutes from '../../../useRoutes';
import { useControlActionModalContent } from '../../../useControlActionModalContent';
import { displayConfirmationModal } from '../displayConfirmationModal';
import { getResolvedRowsToUse } from '../getResolvedRowsToUse';

const log = debugLogger.scope('controlActions');

// Resolves link+params like a `dataset` control's `data` and opens the iframe page.
export const getAppTypeCallback = (
  dataset: Dataset,
  rowsToUse: Record<string, unknown>[],
  currentRow: Record<string, unknown> | undefined,
  selectedVirtualRows: number,
  deselectedRowIds: Set<string>,
  control: DatasetControl,
  datasetId: string | undefined,
  routes: ReturnType<typeof useRoutes>,
  history: ReturnType<typeof useHistory>,
  fromPathname: string,
  // Lazy — built at click time, not on every toolbar render.
  getRequestData: () => DatasetDataApiRequestData | SubsetDataApiRequestData,
  fetchDatasetData: () => Promise<Record<string, unknown>[]>,
  scope: 'dataset' | 'subset',
  openControlModal: ReturnType<typeof useControlActionModalContent>['open'],
  closeControlModal: ReturnType<typeof useControlActionModalContent>['close'],
  setModalLoading: ReturnType<
    typeof useControlActionModalContent
  >['setModalLoading']
) => {
  return async () => {
    try {
      const action = control.action as ApplicationControlAction;
      const controlName = control.name;
      if (!controlName) {
        throw new Error('An app dataset control must have a stable name');
      }

      const shouldDisplayConfirmationModal = displayConfirmationModal(
        control,
        rowsToUse
      );

      if (shouldDisplayConfirmationModal) {
        setModalLoading(true);
      }

      const resolvedRowsToUse = await getResolvedRowsToUse(
        dataset,
        rowsToUse,
        selectedVirtualRows,
        fetchDatasetData,
        deselectedRowIds
      );

      const proceed = async () => {
        const templateData = getDatasetControlDatasetActionTemplateData(
          dataset,
          currentRow,
          resolvedRowsToUse,
          getRequestData()
        );

        const url = await resolveAppActionUrl(
          action,
          templateData as unknown as JSONObject
        );

        /** @debugFeature controlActions Logs an app control action building the external app url: the link and params templates and the resolved url the iframe page receives (the session token never enters the url, so it never appears here). */
        log('app control url built', () => ({
          linkTemplate: action.link,
          paramsTemplate: action.params,
          resolvedUrl: url,
        }));

        const searchParams = new URLSearchParams({
          [EXTERNAL_APP_URL_PARAM]: url,
          [EXTERNAL_APP_CONTROL_PARAM]: controlName,
          [EXTERNAL_APP_SCOPE_PARAM]: scope,
        });
        if (scope === 'subset') {
          searchParams.set(
            EXTERNAL_APP_SUBSET_PARAM,
            String(dataset.datasetId ?? '')
          );
        }

        if (shouldDisplayConfirmationModal) {
          closeControlModal();
        }
        history.push({
          pathname: routes.datasetApp(datasetId ?? ''),
          search: `?${searchParams.toString()}`,
          state: { fromPathname },
        });
      };

      if (shouldDisplayConfirmationModal) {
        setModalLoading(false);
        return openControlModal(dataset, control, resolvedRowsToUse, proceed);
      }

      await proceed();
    } catch (error) {
      setModalLoading(false);
      throw error;
    }
  };
};
